import React, { useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { SKIP_RECAPTCHA_VERIFY } from '../../config/env';
import useFormatter from '../../hooks/useFormatter';
import useOnlyNumbers from '../../hooks/useOnlyNumbers';
import { client as api } from '../../services/api';
import { pushToDataLayer } from '../../types/gtm';
import { digitsOnly, isCpfOrCnpjValid, MSG_DOCUMENTO_INVALIDO } from '../../utils/documento';
import Icon from '../ui/Icon';

function GetOrdersForm({ fetchOrder }) {
  const { garOrder, onlyNumber } = useOnlyNumbers();
  const [document, handleChange] = useFormatter();
  const [erroCapcha, setErroCapcha] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef<any>(null);

  const isGarOrderValid = garOrder.trim() !== '';
  const isDocumentValid = document.trim() !== '';

  const docDigits = useMemo(() => digitsOnly(document), [document]);
  const documentoBloqueiaSubmit =
    docDigits.length === 0
      ? false
      : docDigits.length === 11 || docDigits.length === 14
        ? !isCpfOrCnpjValid(document)
        : true;

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();

    setLoading(true);
    setErroCapcha(null);

    if (!isCpfOrCnpjValid(document)) {
      setErroCapcha(MSG_DOCUMENTO_INVALIDO);
      setLoading(false);
      return;
    }

    try {
      if (!SKIP_RECAPTCHA_VERIFY) {
        const token = await recaptchaRef.current?.executeAsync();
        recaptchaRef.current?.reset();

        if (!token) {
          setErroCapcha('Falha ao validar o reCAPTCHA. Tente novamente.');
          setLoading(false);
          return;
        }

        const { data } = await api.post('/recaptcha/verify', { token });

        if (!data.success) {
          setErroCapcha('Verificação de segurança falhou. Por favor, tente novamente.');
          setLoading(false);
          return;
        }
      }

      pushToDataLayer({
        event: 'select_content',
        content_type: 'agendar-0:numero-do-pedido-e-documento',
      });
      fetchOrder(garOrder, document);
    } catch (error) {
      console.error('Erro inesperado ao validar o reCAPTCHA:', error);
      setErroCapcha('Erro inesperado ao validar o reCAPTCHA.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col px-4 md:px-0 text-left mt-8 md:mt-0">
        <h1 className="title">Agendamento da validação</h1>
        <p className="md:max-w-[768px] mb-5">
          A validação é uma <strong>etapa obrigatória</strong>, porque é o momento em que a
          identidade do titular do certificado digital é confirmada. Somente após esse atendimento,
          o produto é entregue para a instalação.
        </p>
        <p className="mb-5 md:max-w-[768px]">
          Para agendar a validação é necessário informar o <strong>número do pedido</strong> e o{' '}
          <strong>CPF</strong> ou <strong>CNPJ</strong> do titular do certificado digital.
        </p>
      </div>

      <div className="flex sm:flex-row sm:items-start items-center flex-col mt-12 gap-4">
        <div className="flex flex-col gap-9">
          <div className="flex flex-col max-w-[434px] w-full rounded-lg bg-white py-5 px-4 gap-5">
            <h6 className="text-[22px] text-ins-blue font-normal">
              Preencha os dados para agendar a validação
            </h6>

            <form className="flex flex-col h-full w-full gap-8" onSubmit={handleSearch}>
              <div>
                <label htmlFor="garOrder" className="flex flex-col w-full gap-2.5">
                  <span className="flex items-center text-xl text-gray-800 gap-2">
                    <Icon name="Cart" height={30} width={27} fill="var(--ins-blue)" />
                    Número do pedido
                  </span>
                  <input
                    id="garOrder"
                    className="border-gray-400 border rounded-[4px] px-4 h-10 outline-none"
                    onChange={onlyNumber}
                    type="text"
                    placeholder="Digite o número do pedido"
                    maxLength={8}
                    value={garOrder}
                  />
                </label>
              </div>

              <div>
                <label htmlFor="document" className="flex flex-col w-full gap-2.5">
                  <span className="flex items-center text-xl text-gray-800 gap-2">
                    <Icon
                      name="User"
                      height={25}
                      width={25}
                      stroke="var(--ins-blue)"
                      fill="transparent"
                    />
                    CPF ou CNPJ do titular
                  </span>
                  <input
                    id="document"
                    className="border-gray-400 border rounded-[4px] px-4 h-10 outline-none"
                    onChange={(e) => handleChange(e.target.value)}
                    type="text"
                    placeholder="Digite CPF ou CNPJ do titular"
                    maxLength={18}
                    value={document}
                  />
                </label>
              </div>

              {!SKIP_RECAPTCHA_VERIFY ? (
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6LcAZ00rAAAAAB0pgtiJVN7y31J1B5533zsjJ6v8"
                  size="invisible"
                />
              ) : null}

              <button
                className="flex items-center justify-center button-primary mt-auto disabled:opacity-60"
                disabled={
                  !isGarOrderValid || !isDocumentValid || loading || documentoBloqueiaSubmit
                }
                type="submit"
              >
                {loading ? (
                  <Icon
                    className="animate-spin"
                    name="Loading"
                    width={20}
                    height={20}
                    fill="white"
                  />
                ) : (
                  'Agendar'
                )}
              </button>
            </form>
          </div>

          {erroCapcha && (
            <div className="flex gap-2 p-4 bg-gray-100 min-h-20 border-l-4 border-l-ins-blue rounded-[4px]">
              <Icon name="Alert" width={20} height={20} fill="var(--red-500)" />
              <div className="flex flex-col">
                <p className="text-black text-sm font-bold">Atenção</p>
                <p className="text-black text-sm">{erroCapcha}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const GetOrders = ({ fetchOrder }) => <GetOrdersForm fetchOrder={fetchOrder} />;
